"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@workspace/ui/components/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@workspace/ui/components/dropdown-menu";
import { cn } from "@workspace/ui/lib/utils";
import { ArrowUpRight, Check, Minus } from "lucide-react";
import { type ComponentProps, type ComponentType, type CSSProperties, type ReactNode, type PointerEvent as ReactPointerEvent, useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { useMounted } from "../magnus-chat/use-mounted";
import { type FloatingChatInsets, type FloatingChatPresentation, FloatingChatRoot, type FloatingChatSide, SIDEBAR_MAX_WIDTH, SIDEBAR_MIN_WIDTH, useFloatingChat } from "./floating-chat-context";
import { NewChatIcon, PeekCornerIcon, PeekFullIcon, PeekSideIcon } from "./floating-icons";
import { type FloatingChatTriggerAppearance, TRIGGER_APPEARANCE_CLASS, TriggerFace } from "./floating-launcher-appearance";
import { cornerAt, type FreeDropBox, useCornerDrag } from "./floating-launcher-drag";
import { FloatingChatTitleMenu } from "./floating-title-menu";
import { MagnusLogo } from "./magnus-logo";

/**
 * `matchMedia` as an external store: no state, no effects — the subscription
 * IS the media-query listener. SSR snapshot is `false` (desktop-first), so
 * narrow-viewport corrections apply after hydration.
 */
function useMediaQuery(query) {
  const $ = _c(4);
  let t0;
  if ($[0] !== query) {
    t0 = onChange => {
      const mql = window.matchMedia(query);
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    };
    $[0] = query;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const subscribe = t0;
  let t1;
  if ($[2] !== query) {
    t1 = () => window.matchMedia(query).matches;
    $[2] = query;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return useSyncExternalStore(subscribe, t1, _temp);
}

/**
 * Below this width the docked sidebar becomes an overlay sheet. Chosen as
 * default sidebar width (420px) + ~600px of comfortable content ≈ 1024px —
 * also Tailwind's `lg`. At e.g. 800px a docked sidebar would leave the page
 * under 400px, which is the squish this avoids.
 */
function _temp() {
  return false;
}
const SIDEBAR_SHEET_QUERY = "(max-width: 1024px)";

/**
 * Content that lives outside the panel's DOM but belongs to it. Everything the
 * panel opens portals to `document.body`, so an outside-click dismiss has to
 * recognise it or clicking a menu item closes the whole chat:
 *
 * - `data-slot$="-content"` / `-popup` — the Base UI menus, dialogs and popovers.
 * - `data-tippy-root` — the composer's `/` skill and `@` artifact suggestion
 *   popups (TipTap mounts them through tippy.js, which appends to the body and
 *   stamps no `data-slot`).
 * - `data-sonner-toaster` — toasts, which the host renders at its own root.
 */
const PANEL_OWNED_PORTAL_SELECTOR = ['[data-slot$="-content"]', '[data-slot$="-popup"]', "[data-tippy-root]", "[data-sonner-toaster]"].join(", ");

/**
 * Open overlays that own Escape while they are up: menus, dialogs, popovers
 * and listboxes (Base UI marks an open popup `data-open`, Radix
 * `data-state="open"`). Their own Escape handlers sit on `document` too but
 * register later and do not `preventDefault()`, so the panel cannot rely on
 * `defaultPrevented` alone to know the key was theirs.
 */
const ESCAPE_OWNER_ROLES = ["menu", "dialog", "alertdialog", "listbox"];
const ESCAPE_OWNER_SELECTOR = ESCAPE_OWNER_ROLES.map(role => `[role="${role}"]`).join(", ");
const OPEN_ESCAPE_OWNER_SELECTOR = ESCAPE_OWNER_ROLES.flatMap(role => [`[role="${role}"][data-open]`, `[role="${role}"][data-state="open"]`]).join(", ");

/**
 * True when this Escape belongs to an overlay other than the panel: the key
 * came from inside one, or one is open that would take it wherever focus
 * sits — a modal dialog, or a menu or popover in a portal the panel may have
 * opened (it portals out of the panel, and focus may still be on its
 * trigger). A host page's own non-modal open widget, such as an inline
 * listbox, does not hold the key. The panel is itself `role="dialog"`, so it
 * is skipped.
 */
function escapeOwnedByOtherOverlay(event: KeyboardEvent, panel: HTMLElement | null): boolean {
  const target = event.target;
  if (target instanceof Element) {
    const owner = target.closest(ESCAPE_OWNER_SELECTOR);
    if (owner && owner !== panel) {
      return true;
    }
  }
  for (const overlay of document.querySelectorAll(OPEN_ESCAPE_OWNER_SELECTOR)) {
    if (overlay !== panel && (overlay.getAttribute("aria-modal") === "true" || overlay.closest(PANEL_OWNED_PORTAL_SELECTOR))) {
      return true;
    }
  }
  return false;
}

/**
 * Portals children to `document.body` so the launcher and panel float above
 * the app regardless of where `<FloatingChat.Root>` is mounted in the tree.
 */
function BodyPortal(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  const mounted = useMounted();
  if (!mounted) {
    return null;
  }
  let t1;
  if ($[0] !== children) {
    t1 = createPortal(children, document.body);
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
const SIDE_ANCHOR: Record<FloatingChatSide, string> = {
  "bottom-right": "bottom-4 right-4",
  "bottom-left": "bottom-4 left-4",
  "top-right": "top-4 right-4",
  "top-left": "top-4 left-4"
};

/*
 * Corner anchors that keep clear of host chrome (`insets`, published as the
 * custom properties below) and of the device's safe areas. Used once a root
 * opts into dragging or insets, so default hosts keep their exact layout.
 */
const CLEAR_EDGE = {
  top: "top-[calc(1rem+var(--magnus-floating-inset-top,0px)+env(safe-area-inset-top,0px))]",
  bottom: "bottom-[calc(1rem+var(--magnus-floating-inset-bottom,0px)+env(safe-area-inset-bottom,0px))]",
  left: "left-[calc(1rem+env(safe-area-inset-left,0px))]",
  right: "right-[calc(1rem+env(safe-area-inset-right,0px))]"
};
const CLEAR_ANCHOR: Record<FloatingChatSide, string> = {
  "bottom-right": `${CLEAR_EDGE.bottom} ${CLEAR_EDGE.right}`,
  "bottom-left": `${CLEAR_EDGE.bottom} ${CLEAR_EDGE.left}`,
  "top-right": `${CLEAR_EDGE.top} ${CLEAR_EDGE.right}`,
  "top-left": `${CLEAR_EDGE.top} ${CLEAR_EDGE.left}`
};
const CLEAR_FLOATING_HEIGHT = "h-[min(600px,calc(100dvh-2rem-var(--magnus-floating-inset-top,0px)-var(--magnus-floating-inset-bottom,0px)))]";
function isLeft(side: FloatingChatSide): boolean {
  return side.endsWith("left");
}
function isTop(side: FloatingChatSide): boolean {
  return side.startsWith("top");
}

/** The corner anchor for `side`, clear of host chrome when `clear`. */
function cornerAnchor(side: FloatingChatSide, clear: boolean): string {
  return (clear ? CLEAR_ANCHOR : SIDE_ANCHOR)[side];
}

/** The 1rem corner gap along `side`'s horizontal edge, plus its safe area. */
function horizontalGapCss(side: FloatingChatSide, clear: boolean): string {
  if (!clear) {
    return "1rem";
  }
  return `calc(1rem + env(safe-area-inset-${isLeft(side) ? "left" : "right"}, 0px))`;
}

/** Publishes `insets` as the custom properties {@link CLEAR_EDGE} reads. */
function insetVars(insets: FloatingChatInsets | undefined): CSSProperties {
  return {
    "--magnus-floating-inset-bottom": insets?.bottom,
    "--magnus-floating-inset-top": insets?.top
  } as CSSProperties;
}

/** CSS width for the sidebar at `px`, never exceeding most of the viewport. */
function sidebarWidthCss(px: number): string {
  return `min(${px}px, 92vw)`;
}

/**
 * Where a freely dragged launcher sits: its centre as fractions (0–1) of the
 * viewport's width and height, so it keeps its place across window sizes.
 */
export interface FloatingChatLauncherPosition {
  x: number;
  y: number;
}

/** The launcher's edge-to-edge size and its minimum gap from the viewport. */
const LAUNCHER_PX = 56;
const LAUNCHER_GAP_PX = 16;

/** Inline position for a launcher placed at `position`, kept on screen. */
function freePositionStyle(position: FloatingChatLauncherPosition) {
  const half = LAUNCHER_PX / 2;
  const far = LAUNCHER_PX + LAUNCHER_GAP_PX;
  return {
    left: `clamp(${LAUNCHER_GAP_PX}px, calc(${position.x * 100}vw - ${half}px), calc(100vw - ${far}px))`,
    top: `clamp(calc(${LAUNCHER_GAP_PX}px + var(--magnus-floating-inset-top, 0px)), calc(${position.y * 100}dvh - ${half}px), calc(100dvh - ${far}px - var(--magnus-floating-inset-bottom, 0px)))`,
    right: "auto",
    bottom: "auto"
  };
}
function clampFraction(value: number): number {
  return Math.min(Math.max(value, 0), 1);
}

/** How {@link FloatingChatTrigger} presents itself while the panel is open. */
export type FloatingChatTriggerBehavior = "hide-when-open" | "persistent";

/**
 * Launcher button — a fixed, corner-anchored FAB (portaled to body) that
 * toggles the panel. Defaults to the Magnus mark; pass `children` to supply
 * your own icon/label. For a launcher elsewhere in the layout, render your
 * own control and call `useFloatingChat().actions.toggle()` instead.
 *
 * `behavior` (default `"hide-when-open"`) controls the open state: the
 * default fades the FAB out while the panel is open; `"persistent"` keeps it
 * rendered and clickable as a toggle (nav-button model) with pressed styling
 * (`data-state="open"` / `aria-pressed`) and a label swap. While the docked
 * sidebar is open the persistent trigger offsets inward by the live sidebar
 * width — the FAB is `fixed` and the sidebar's page push insets `<html>`,
 * which fixed elements ignore, so without the offset it would sit under the
 * panel.
 *
 * Passing `onPositionChange` lets the launcher be dragged anywhere instead of
 * snapping to a corner: it stays where it is let go, and the host keeps that
 * spot in `position` (null for the corner). The panel still opens in the
 * corner nearest the launcher. Needs the root's `draggable`.
 */
export function FloatingChatTrigger({
  appearance = "solid",
  behavior = "hide-when-open",
  children,
  className,
  onClick,
  onPointerCancel,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPositionChange,
  position,
  ref,
  style,
  ...props
}: ComponentProps<typeof Button> & {
  /** Material the FAB is drawn in. Defaults to `"solid"`. */
  appearance?: FloatingChatTriggerAppearance;
  behavior?: FloatingChatTriggerBehavior;
  /** A freely dragged launcher's spot; null or absent sits in the corner. */
  position?: FloatingChatLauncherPosition | null;
  /** Opts into free dragging; called with where the launcher was let go. */
  onPositionChange?: (position: FloatingChatLauncherPosition) => void;
}) {
  const {
    state: {
      open,
      presentation,
      sidebarWidth
    },
    actions: {
      setSide,
      toggle
    },
    meta: {
      clearsChrome,
      draggable,
      insets,
      panelId,
      side
    }
  } = useFloatingChat();
  const onFreeRelease = useMemo(() => onPositionChange ? (box: FreeDropBox) => {
    const centreX = box.left + box.width / 2;
    const centreY = box.top + box.height / 2;
    // The panel opens in the corner nearest the launcher.
    setSide(cornerAt(centreX, centreY));
    onPositionChange({
      x: clampFraction(centreX / window.innerWidth),
      y: clampFraction(centreY / window.innerHeight)
    });
  } : undefined, [onPositionChange, setSide]);
  const drag = useCornerDrag({
    enabled: draggable,
    onFreeRelease,
    onSideChange: setSide,
    side
  });
  const dragRef = drag.ref;
  const setRef = useCallback((node: HTMLButtonElement | null) => {
    dragRef.current = node;
    if (typeof ref === "function") {
      return ref(node);
    }
    if (ref) {
      ref.current = node;
    }
  }, [dragRef, ref]);
  const persistent = behavior === "persistent";
  // Below the sheet breakpoint the sidebar overlays (scrim) instead of
  // docking, so the trigger keeps its corner and the scrim (z-40) covers
  // it — the trigger drops below the scrim while the sheet is open, so a
  // corner tap hits the scrim (closing the sheet) instead of the FAB.
  const isNarrow = useMediaQuery(SIDEBAR_SHEET_QUERY);
  const sheeted = open && presentation === "sidebar" && isNarrow;
  const docked = open && presentation === "sidebar" && !isNarrow;
  const offsetCss = persistent && docked ? `calc(${horizontalGapCss(side, clearsChrome)} + ${sidebarWidthCss(sidebarWidth)})` : undefined;
  return <BodyPortal>
      <Button aria-controls={panelId} aria-description={draggable ? onPositionChange ? "Drag to move it anywhere." : "Drag to move it to another corner, or use the chat's layout menu." : undefined} aria-expanded={open} aria-label={persistent && open ? "Close Magnus chat" : "Open Magnus chat"} aria-pressed={persistent ? open : undefined} className={cn("fixed p-0", "size-14 rounded-full", drag.active && "touch-none select-none", drag.dragging && "cursor-grabbing", sheeted ? "z-30" : "z-50", persistent ?
    // Positional transition matches the panel's 200ms ease so the
    // docked-sidebar offset reads as the panel pushing the FAB.
    "transition-[transform,opacity,left,right,color] duration-200 ease-out" : "transition-[transform,opacity,color] duration-[250ms] ease-[cubic-bezier(0.34,1.56,0.64,1)]", TRIGGER_APPEARANCE_CLASS[appearance], cornerAnchor(side, clearsChrome), persistent ? "data-[state=open]:ring-2 data-[state=open]:ring-ring/70" : open && "pointer-events-none scale-90 opacity-0", className)} data-appearance={appearance} data-dragging={drag.dragging || undefined} data-floating-chat-launcher data-state={open ? "open" : "closed"} onClick={event => {
      if (drag.consumeDragClick()) {
        return;
      }
      if (onClick) {
        onClick(event);
      } else {
        toggle();
      }
    }} onPointerCancel={event_0 => {
      drag.handlers.onPointerCancel(event_0);
      onPointerCancel?.(event_0);
    }} onPointerDown={event_1 => {
      drag.handlers.onPointerDown(event_1);
      onPointerDown?.(event_1);
    }} onPointerMove={event_2 => {
      drag.handlers.onPointerMove(event_2);
      onPointerMove?.(event_2);
    }} onPointerUp={event_3 => {
      drag.handlers.onPointerUp(event_3);
      onPointerUp?.(event_3);
    }} ref={setRef} size="icon" style={{
      ...(clearsChrome ? insetVars(insets) : undefined),
      ...(position && onPositionChange ? freePositionStyle(position) : undefined),
      ...style,
      ...(offsetCss ? {
        [isLeft(side) ? "left" : "right"]: offsetCss
      } : undefined)
    }} type="button" {...props}>
        <TriggerFace appearance={appearance}>{children}</TriggerFace>
      </Button>
    </BodyPortal>;
}

/**
 * Size + chrome per presentation (position + sidebar width applied
 * separately). Floating shrinks to the viewport on small screens; the docked
 * sidebar is flat (no shadow) and resizable.
 *
 * `sheet` is the derived narrow-viewport form of `sidebar`: hosts never
 * select it, so it lives in a local superset type instead of the public
 * `FloatingChatPresentation` union (and never round-trips through the
 * persistence seam).
 */
type PanelSurface = FloatingChatPresentation | "sheet";
const PANEL_SURFACE: Record<PanelSurface, string> = {
  floating: "h-[min(600px,calc(100dvh-2rem))] w-[min(540px,calc(100vw-2rem))] rounded-[1.75rem] border shadow-2xl",
  sidebar: "h-dvh rounded-none",
  fullscreen: "inset-0 h-dvh w-screen rounded-none border-0",
  sheet: "h-dvh shadow-2xl"
};

/** Anchor (position) per presentation, resolved against the launcher side. */
function panelAnchor(presentation: PanelSurface, side: FloatingChatSide, clear: boolean): string {
  if (presentation === "fullscreen") {
    return "";
  }
  if (presentation === "sheet") {
    // Edge rounding + border on the page-facing side only.
    return isLeft(side) ? "top-0 left-0 rounded-r-2xl border-r" : "top-0 right-0 rounded-l-2xl border-l";
  }
  if (presentation === "sidebar") {
    return isLeft(side) ? "top-0 left-0 border-r" : "top-0 right-0 border-l";
  }
  return cornerAnchor(side, clear);
}
const FLOATING_ORIGIN: Record<FloatingChatSide, string> = {
  "bottom-right": "origin-bottom-right",
  "bottom-left": "origin-bottom-left",
  "top-right": "origin-top-right",
  "top-left": "origin-top-left"
};

/** Transform origin per presentation, for the open/close animation. */
function panelOrigin(presentation: PanelSurface, side: FloatingChatSide): string {
  if (presentation === "sidebar" || presentation === "sheet") {
    return isLeft(side) ? "origin-left" : "origin-right";
  }
  if (presentation === "fullscreen") {
    return "origin-center";
  }
  return FLOATING_ORIGIN[side];
}

/** Closed-state transform per presentation (slide-out direction). */
function panelClosedTransform(presentation: PanelSurface, side: FloatingChatSide): string {
  if (presentation === "sheet") {
    // Slides fully off the anchored edge so no sliver peeks over the page.
    return isLeft(side) ? "pointer-events-none -translate-x-full opacity-0" : "pointer-events-none translate-x-full opacity-0";
  }
  if (presentation === "sidebar") {
    return isLeft(side) ? "pointer-events-none -translate-x-4 opacity-0" : "pointer-events-none translate-x-4 opacity-0";
  }
  if (presentation === "fullscreen") {
    return "pointer-events-none scale-95 opacity-0";
  }
  return isTop(side) ? "pointer-events-none -translate-y-2 scale-95 opacity-0" : "pointer-events-none translate-y-2 scale-95 opacity-0";
}

/**
 * Reflows page content while the sidebar presentation is open by insetting the
 * document root (the panel is `position: fixed`, so it sits in the freed
 * gutter). Mirrors Notion's sidebar mode pushing the page instead of overlaying
 * it. No-op for floating/fullscreen.
 *
 * We inset `<html>` rather than `<body>`: overlay primitives (base-ui menus,
 * dialogs) lock scroll by mutating `body` padding, which would clobber ours
 * when the layout menu closes. Tailwind's preflight makes the root `border-box`,
 * so the inset shrinks content without introducing a horizontal scrollbar.
 */
function useSidebarRootInset(active, side, widthCss) {
  const $ = _c(5);
  let t0;
  let t1;
  if ($[0] !== active || $[1] !== side || $[2] !== widthCss) {
    t0 = () => {
      if (!active || typeof document === "undefined") {
        return;
      }
      const property = isLeft(side) ? "padding-left" : "padding-right";
      const root = document.documentElement;
      const previous = root.style.getPropertyValue(property);
      root.style.setProperty(property, widthCss);
      return () => {
        if (previous) {
          root.style.setProperty(property, previous);
        } else {
          root.style.removeProperty(property);
        }
      };
    };
    t1 = [active, side, widthCss];
    $[0] = active;
    $[1] = side;
    $[2] = widthCss;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
}

/**
 * Drag handle on the docked sidebar's inner edge. Resizes between
 * {@link SIDEBAR_MIN_WIDTH} and {@link SIDEBAR_MAX_WIDTH}; capped at the
 * viewport via {@link sidebarWidthCss}.
 */
function SidebarResizeHandle(t0) {
  const $ = _c(10);
  const {
    side,
    onResize
  } = t0;
  let t1;
  if ($[0] !== onResize || $[1] !== side) {
    t1 = event => {
      event.preventDefault();
      const move = e => {
        const raw = isLeft(side) ? e.clientX : window.innerWidth - e.clientX;
        const max = Math.min(SIDEBAR_MAX_WIDTH, window.innerWidth * 0.92);
        onResize(Math.max(SIDEBAR_MIN_WIDTH, Math.min(raw, max)));
      };
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        document.body.style.userSelect = "";
      };
      document.body.style.userSelect = "none";
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    };
    $[0] = onResize;
    $[1] = side;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const onPointerDown = t1;
  let t2;
  if ($[3] !== side) {
    t2 = isLeft(side) ? "right-0" : "left-0";
    $[3] = side;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = cn("absolute inset-y-0 z-20 w-1.5 cursor-col-resize transition-colors hover:bg-primary/40", t2);
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== onPointerDown || $[8] !== t3) {
    t4 = <div aria-hidden={true} className={t3} onPointerDown={onPointerDown} />;
    $[7] = onPointerDown;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

/**
 * What inside a draggable header is a control rather than grip: a press on
 * one of these never starts a panel drag. Exported for hosts that bring
 * their own header and relay its presses onto the panel's grip.
 */
export const FLOATING_CHAT_HEADER_CONTROL_SELECTOR = "button, a, input, textarea, select, [role='button'], [role='menuitem'], [contenteditable='true']";
function isHeaderGrip(target: Element): boolean {
  return target.closest("[data-floating-chat-header]") !== null && target.closest(FLOATING_CHAT_HEADER_CONTROL_SELECTOR) === null;
}

/**
 * The floating surface container. Reads open + presentation from context and
 * sizes/positions itself accordingly (resizable docked sidebar, floating corner
 * card that shrinks with the viewport, or fullscreen). Stays mounted while
 * closed (hidden + `inert`) so the chat session inside persists across
 * open/close. Closes on `Escape`; the floating presentation also closes on
 * outside click (the root's `dismissOnOutsideClick`, default on).
 */
export function FloatingChatPanel({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) {
  const {
    state: {
      open,
      presentation,
      sidebarWidth
    },
    actions: {
      close,
      setSide,
      setSidebarWidth
    },
    meta: {
      clearsChrome,
      dismissOnOutsideClick,
      draggable,
      insets,
      panelId,
      side
    }
  } = useFloatingChat();
  const panelRef = useRef<HTMLDivElement>(null);
  const drag = useCornerDrag<HTMLDivElement>({
    enabled: draggable && open && presentation === "floating",
    onSideChange: setSide,
    side,
    startsDrag: isHeaderGrip
  });
  const dragRef = drag.ref;
  const setPanelNode = useCallback((node: HTMLDivElement | null) => {
    panelRef.current = node;
    dragRef.current = node;
  }, [dragRef]);
  const isSidebar = presentation === "sidebar";
  const widthCss = sidebarWidthCss(sidebarWidth);

  // On narrow viewports the docked sidebar can't squeeze the page (there is
  // no room), so it degrades to a hovering sheet — most of the width over a
  // dimmed scrim, page peeking behind — derived during render from the media
  // query, no effect state.
  const isNarrow = useMediaQuery(SIDEBAR_SHEET_QUERY);
  const sidebarAsSheet = isSidebar && isNarrow;
  const surfacePresentation: PanelSurface = sidebarAsSheet ? "sheet" : presentation;

  // Outside-click dismiss: floating presentation only, and only when the
  // root opts in (default). The docked sidebar is persistent chrome; the
  // sheet closes via its scrim. Menus/dialogs opened from inside the panel
  // portal out of it — their clicks are outside the panel DOM but must not
  // dismiss, so portaled overlay content is ignored too.
  useEffect(() => {
    if (!open || presentation !== "floating" || !dismissOnOutsideClick) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (panelRef.current?.contains(target)) {
        return;
      }
      if (target.closest("[data-floating-chat-launcher]")) {
        return;
      }
      if (target.closest(PANEL_OWNED_PORTAL_SELECTOR)) {
        return;
      }
      close();
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, presentation, dismissOnOutsideClick, close]);
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event_0: KeyboardEvent) => {
      // Whatever is innermost gets Escape first. A mention picker, menu or
      // dialog inside the panel calls `preventDefault()` when it consumes the
      // key, and closing the whole panel on that same keystroke would throw
      // away the composition the user was in the middle of. Base UI menus
      // and dialogs (the panel's own header menus, or a host dialog) close
      // on Escape without preventing it, and their listener runs after this
      // one — so an open overlay other than the panel owns the key too.
      if (event_0.key !== "Escape" || event_0.defaultPrevented) {
        return;
      }
      if (escapeOwnedByOtherOverlay(event_0, panelRef.current)) {
        return;
      }
      close();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  // Root inset only applies when the sidebar actually docks (wide enough to
  // push the page). The overlay sheet overlays instead.
  useSidebarRootInset(open && isSidebar && !isNarrow, side, widthCss);
  return <BodyPortal>
      {sidebarAsSheet ?
    // Scrim: dims the page and closes the sheet on click. Fades with the
    // panel's transition; inert to pointer events once hidden.
    <div aria-hidden className={cn("fixed inset-0 z-40 bg-black/50 transition-opacity duration-200", open ? "opacity-100" : "pointer-events-none opacity-0")} onClick={close} /> : null}
      <div aria-label="Magnus chat" aria-modal={sidebarAsSheet} className={cn("fixed z-50 flex flex-col overflow-hidden border-border bg-background text-foreground", "transition-[opacity,transform] duration-200 ease-out", panelAnchor(surfacePresentation, side, clearsChrome), panelOrigin(surfacePresentation, side), PANEL_SURFACE[surfacePresentation], clearsChrome && surfacePresentation === "floating" && CLEAR_FLOATING_HEIGHT, open ? "translate-x-0 translate-y-0 scale-100 opacity-100" : panelClosedTransform(surfacePresentation, side), drag.active && "[&_[data-floating-chat-header]]:cursor-grab [&_[data-floating-chat-header]]:touch-none", drag.dragging && "select-none [&_[data-floating-chat-header]]:cursor-grabbing", className)} data-dragging={drag.dragging || undefined} id={panelId}
    // `inert` (React 19) drops the closed panel from tab order + a11y tree
    // while keeping it mounted so the conversation state survives.
    inert={!open} {...drag.handlers} ref={setPanelNode} role="dialog" style={{
      ...(clearsChrome ? insetVars(insets) : undefined),
      ...(isSidebar ? {
        // Sheet caps at the remembered sidebar width but always leaves
        // a page sliver visible on narrow viewports.
        width: sidebarAsSheet ? `min(88vw, ${widthCss})` : widthCss
      } : undefined)
    }}>
        {isSidebar && !sidebarAsSheet ? <SidebarResizeHandle onResize={setSidebarWidth} side={side} /> : null}
        {children}
      </div>
    </BodyPortal>;
}

/**
 * Header bar for the panel. Compose your own controls as children, or render
 * it empty (`<FloatingChat.Header />`) for the default arrangement — Magnus
 * mark, the active conversation's title as a dropdown switcher (a static
 * title when no `history` source is injected on the root), then new-chat,
 * layout, and minimize controls:
 *
 * ```tsx
 * <FloatingChat.Panel>
 *   <FloatingChat.Header />
 *   <FloatingChat.Body><MagnusChat ... /></FloatingChat.Body>
 * </FloatingChat.Panel>
 * ```
 *
 * Borderless (Notion-style): instead of a hard divider, the body fades content
 * under the header via a top gradient mask. Sits above the body in the stacking
 * order so the controls stay clickable over the fade.
 */
export function FloatingChatHeader(t0) {
  const $ = _c(7);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("relative z-10 flex shrink-0 items-center gap-1 py-2 pr-3 pl-3 sm:pl-4", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = children ?? <FloatingChatDefaultHeaderContent />;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <header className={t1} data-floating-chat-header={true}>{t2}</header>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/**
 * The default header arrangement, driven entirely by root-injected sources
 * (`history`, `activeChatId`, `onLayoutSelect`). Exported for hosts that want
 * the default content inside a custom-styled header element.
 */
export function FloatingChatDefaultHeaderContent() {
  const $ = _c(11);
  const {
    meta: t0,
    sources: t1
  } = useFloatingChat();
  const {
    onLayoutSelect
  } = t0;
  const {
    history
  } = t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <MagnusLogo className="-mt-0.5 size-6 shrink-0 text-magnus" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  let t4;
  if ($[1] !== history) {
    t3 = <div className="mr-auto flex min-w-0 items-center gap-0.5 sm:gap-1">{t2}{history ? <FloatingChatTitleMenu /> : <span className="truncate pl-2 font-medium text-sm">Magnus</span>}</div>;
    t4 = history ? <FloatingChatNewChatButton onClick={history.onNewChat} /> : null;
    $[1] = history;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== onLayoutSelect) {
    t5 = <FloatingChatLayoutMenu onSelect={onLayoutSelect} />;
    $[4] = onLayoutSelect;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <FloatingChatMinimizeButton />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t3 || $[8] !== t4 || $[9] !== t5) {
    t7 = <>{t3}{t4}{t5}{t6}</>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}
export function FloatingChatTitle(t0) {
  const $ = _c(7);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("mr-auto flex items-center gap-2 font-medium text-sm", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = children ?? <><MagnusLogo className="size-4" />Magnus</>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <span className={t1}>{t2}</span>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/**
 * Scrollable body region for the chat surface. Drop a self-driving
 * `<MagnusChat />` (or any `@magnus/react/chat` composition) inside.
 */
export function FloatingChatBody(t0) {
  const $ = _c(6);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("relative min-h-0 flex-1 overflow-hidden", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="pointer-events-none absolute inset-x-0 top-0 z-[5] h-4 bg-gradient-to-b from-background to-transparent" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children || $[4] !== t1) {
    t3 = <div className={t1}>{t2}{children}</div>;
    $[3] = children;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
type LayoutOption = {
  value: FloatingChatPresentation;
  label: string;
  Icon: ComponentType<{
    className?: string;
  }>;
};
const LAYOUT_OPTIONS: readonly LayoutOption[] = [{
  value: "sidebar",
  label: "Sidebar",
  Icon: PeekSideIcon
}, {
  value: "floating",
  label: "Floating",
  Icon: PeekCornerIcon
}, {
  value: "fullscreen",
  label: "Full screen",
  Icon: PeekFullIcon
}];
const CORNER_OPTIONS: readonly {
  value: FloatingChatSide;
  label: string;
}[] = [{
  value: "top-left",
  label: "Top left"
}, {
  value: "top-right",
  label: "Top right"
}, {
  value: "bottom-left",
  label: "Bottom left"
}, {
  value: "bottom-right",
  label: "Bottom right"
}];

/**
 * The single presentation switcher (Notion-style): a ghost icon button whose
 * glyph reflects the current layout, opening a menu of Sidebar / Floating /
 * Full screen with a check on the active one.
 *
 * Pass `onSelect` to intercept a choice — e.g. routing "Full screen" to a
 * dedicated chat page instead of the overlay presentation. Return `false` (or
 * nothing after handling) to suppress the built-in `setPresentation`.
 *
 * The root's `allowedPresentations` filters the choices; with one (or zero)
 * allowed layouts there is nothing to switch, so the menu renders nothing —
 * unless the root is `draggable`, when it also lists the four corners: the
 * way to move the launcher without dragging it.
 */
export function FloatingChatLayoutMenu(t0) {
  const $ = _c(54);
  const {
    className,
    onSelect
  } = t0;
  const {
    state: t1,
    actions: t2,
    meta: t3
  } = useFloatingChat();
  const {
    presentation
  } = t1;
  const {
    setPresentation,
    setSide
  } = t2;
  const {
    allowedPresentations,
    draggable,
    onOpenInChat,
    side
  } = t3;
  let T0;
  let T1;
  let showLayouts;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== allowedPresentations || $[1] !== className || $[2] !== draggable || $[3] !== onOpenInChat || $[4] !== onSelect || $[5] !== presentation || $[6] !== setPresentation) {
    t8 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t9;
      if ($[15] !== allowedPresentations || $[16] !== onOpenInChat) {
        t9 = option => (!allowedPresentations || allowedPresentations.includes(option.value)) && !(onOpenInChat && option.value === "fullscreen");
        $[15] = allowedPresentations;
        $[16] = onOpenInChat;
        $[17] = t9;
      } else {
        t9 = $[17];
      }
      const options = LAYOUT_OPTIONS.filter(t9);
      let t10;
      if ($[18] !== presentation) {
        t10 = LAYOUT_OPTIONS.find(option_0 => option_0.value === presentation);
        $[18] = presentation;
        $[19] = t10;
      } else {
        t10 = $[19];
      }
      const active = t10;
      const TriggerIcon = active?.Icon ?? PeekCornerIcon;
      let t11;
      if ($[20] !== onSelect || $[21] !== setPresentation) {
        t11 = value => {
          if (onSelect?.(value) === false) {
            return;
          }
          setPresentation(value);
        };
        $[20] = onSelect;
        $[21] = setPresentation;
        $[22] = t11;
      } else {
        t11 = $[22];
      }
      const handleSelect = t11;
      showLayouts = options.length + (onOpenInChat ? 1 : 0) > 1;
      if (!(showLayouts || draggable || onOpenInChat)) {
        t8 = null;
        break bb0;
      }
      T1 = DropdownMenu;
      let t12;
      if ($[23] !== className) {
        t12 = cn("size-8", className);
        $[23] = className;
        $[24] = t12;
      } else {
        t12 = $[24];
      }
      let t13;
      if ($[25] !== t12) {
        t13 = <Button aria-label="Change layout" className={t12} size="icon" title="Layout" type="button" variant="ghost" />;
        $[25] = t12;
        $[26] = t13;
      } else {
        t13 = $[26];
      }
      let t14;
      if ($[27] !== TriggerIcon) {
        t14 = <TriggerIcon className="size-5" />;
        $[27] = TriggerIcon;
        $[28] = t14;
      } else {
        t14 = $[28];
      }
      if ($[29] !== t13 || $[30] !== t14) {
        t7 = <DropdownMenuTrigger render={t13}>{t14}</DropdownMenuTrigger>;
        $[29] = t13;
        $[30] = t14;
        $[31] = t7;
      } else {
        t7 = $[31];
      }
      T0 = DropdownMenuContent;
      t4 = "end";
      t5 = "w-44";
      t6 = showLayouts ? options.map(t15 => {
        const {
          value: value_0,
          label,
          Icon
        } = t15;
        return <DropdownMenuItem key={value_0} onClick={() => handleSelect(value_0)}><Icon className="size-5 text-muted-foreground" /><span>{label}</span>{presentation === value_0 && <Check className="ml-auto size-4" />}</DropdownMenuItem>;
      }) : null;
    }
    $[0] = allowedPresentations;
    $[1] = className;
    $[2] = draggable;
    $[3] = onOpenInChat;
    $[4] = onSelect;
    $[5] = presentation;
    $[6] = setPresentation;
    $[7] = T0;
    $[8] = T1;
    $[9] = showLayouts;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    T0 = $[7];
    T1 = $[8];
    showLayouts = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
  }
  if (t8 !== Symbol.for("react.early_return_sentinel")) {
    return t8;
  }
  let t9;
  if ($[32] !== onOpenInChat) {
    t9 = onOpenInChat ? <DropdownMenuItem onClick={onOpenInChat}><ArrowUpRight className="size-5 text-muted-foreground" /><span>Open in chat</span></DropdownMenuItem> : null;
    $[32] = onOpenInChat;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== draggable || $[35] !== onOpenInChat || $[36] !== showLayouts) {
    t10 = (showLayouts || onOpenInChat) && draggable ? <DropdownMenuSeparator /> : null;
    $[34] = draggable;
    $[35] = onOpenInChat;
    $[36] = showLayouts;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== draggable || $[39] !== setSide || $[40] !== side) {
    t11 = draggable ? <DropdownMenuGroup><DropdownMenuLabel>Move to corner</DropdownMenuLabel>{CORNER_OPTIONS.map(t12 => {
        const {
          value: value_1,
          label: label_0
        } = t12;
        return <DropdownMenuItem key={value_1} onClick={() => setSide(value_1)}><span>{label_0}</span>{side === value_1 && <Check className="ml-auto size-4" />}</DropdownMenuItem>;
      })}</DropdownMenuGroup> : null;
    $[38] = draggable;
    $[39] = setSide;
    $[40] = side;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== T0 || $[43] !== t10 || $[44] !== t11 || $[45] !== t4 || $[46] !== t5 || $[47] !== t6 || $[48] !== t9) {
    t12 = <T0 align={t4} className={t5}>{t6}{t9}{t10}{t11}</T0>;
    $[42] = T0;
    $[43] = t10;
    $[44] = t11;
    $[45] = t4;
    $[46] = t5;
    $[47] = t6;
    $[48] = t9;
    $[49] = t12;
  } else {
    t12 = $[49];
  }
  let t13;
  if ($[50] !== T1 || $[51] !== t12 || $[52] !== t7) {
    t13 = <T1>{t7}{t12}</T1>;
    $[50] = T1;
    $[51] = t12;
    $[52] = t7;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  return t13;
}

/**
 * Minimize the panel back to the launcher. The panel stays mounted (and
 * `inert`) so the conversation persists.
 */
export function FloatingChatMinimizeButton(t0) {
  const $ = _c(10);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const {
    actions: t1
  } = useFloatingChat();
  const {
    close
  } = t1;
  let t2;
  if ($[3] !== className) {
    t2 = cn("size-8", className);
    $[3] = className;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Minus className="size-5" />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== close || $[7] !== props || $[8] !== t2) {
    t4 = <Button aria-label="Minimize chat" className={t2} onClick={close} size="icon" title="Minimize" type="button" variant="ghost" {...props}>{t3}</Button>;
    $[6] = close;
    $[7] = props;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

/**
 * Starts a new conversation. The shell doesn't own the chat session, so wire
 * `onClick` to reset yours (e.g. bump a `key` on the `<MagnusChat>` inside the
 * body to remount it with a fresh ephemeral id).
 */
export function FloatingChatNewChatButton(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("size-8", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <NewChatIcon className="size-5" />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t1) {
    t3 = <Button aria-label="New chat" className={t1} size="icon" title="New chat" type="button" variant="ghost" {...props}>{t2}</Button>;
    $[6] = props;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

/**
 * Compound entry point for the floating chat shell. Compose the launcher,
 * panel, the new-chat control, the layout switcher, and minimize; drop a chat
 * (e.g. `<MagnusChat />`) in the body. State (open + presentation) is
 * dependency-injected by `FloatingChat.Root` — swap the provider, keep the UI.
 *
 * ```tsx
 * <FloatingChat.Root history={history} activeChatId={chatId}>
 *   <FloatingChat.Trigger />
 *   <FloatingChat.Panel>
 *     <FloatingChat.Header />
 *     <FloatingChat.Body>
 *       <MagnusChat />
 *     </FloatingChat.Body>
 *   </FloatingChat.Panel>
 * </FloatingChat.Root>
 * ```
 *
 * `<FloatingChat.Header />` with no children renders the default arrangement
 * (mark, title dropdown, new-chat / layout / minimize). Pass children to
 * compose your own from `Title`, `TitleMenu`, `NewChat`, `LayoutMenu`, and
 * `Minimize`.
 */
export const FloatingChat = {
  Root: FloatingChatRoot,
  Trigger: FloatingChatTrigger,
  Panel: FloatingChatPanel,
  Header: FloatingChatHeader,
  Title: FloatingChatTitle,
  TitleMenu: FloatingChatTitleMenu,
  Body: FloatingChatBody,
  NewChat: FloatingChatNewChatButton,
  LayoutMenu: FloatingChatLayoutMenu,
  Minimize: FloatingChatMinimizeButton
} as const;